<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="活动">
        <!-- 活动列表「举报」入口带 activityId 跳过来，这里必须能看见并改选，否则筛完不知在筛什么 -->
        <el-select
          v-model="query.activityId"
          placeholder="请选择活动"
          clearable
          filterable
          class="!w-200px"
          @change="search"
        >
          <el-option v-for="item in activityList" :key="item.id" :label="item.title" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="活动标题">
        <el-input v-model="query.activityTitle" clearable class="!w-180px" placeholder="活动标题关键字" />
      </el-form-item>
      <el-form-item label="举报人ID">
        <el-input v-model="query.reporterUserId" clearable class="!w-140px" placeholder="用户编号" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.auditStatus" clearable class="!w-140px" placeholder="全部">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.ACTIVITY_REPORT_AUDIT_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">查询</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="activityTitle" label="活动" min-width="180" show-overflow-tooltip />
      <el-table-column label="举报人" min-width="140">
        <template #default="{ row }">
          <span>{{ row.reporterNickname || row.reporterUserId }}</span>
        </template>
      </el-table-column>
      <el-table-column label="类型" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">
          {{ row.reportTypeLabel || getDictLabel(DICT_TYPE.ACTIVITY_REPORT_TYPE, row.reportType) }}
        </template>
      </el-table-column>
      <el-table-column prop="content" label="说明" min-width="200" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTagType(row.auditStatus)">
            {{ getDictLabel(DICT_TYPE.ACTIVITY_REPORT_AUDIT_STATUS, row.auditStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="auditResult" label="处理结果" min-width="160" show-overflow-tooltip />
      <el-table-column label="举报时间" min-width="170">
        <template #default="{ row }">
          {{ row.createTimeText || row.createTime || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openView(row)">查看</el-button>
          <el-button
            v-if="Number(row.auditStatus) === 0"
            v-hasPermi="['activity:report:handle']"
            link
            type="warning"
            @click="open(row)"
          >
            处理
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog v-model="visible" :title="viewOnly ? '举报详情' : '处理举报'" width="520px">
    <el-form :model="form" label-width="90px">
      <el-form-item label="活动">
        <div>{{ current?.activityTitle || '-' }}</div>
      </el-form-item>
      <el-form-item label="举报类型">
        <div>
          {{
            current?.reportTypeLabel ||
            getDictLabel(DICT_TYPE.ACTIVITY_REPORT_TYPE, current?.reportType) ||
            '-'
          }}
        </div>
      </el-form-item>
      <el-form-item label="举报内容">
        <div class="text-13px leading-22px text-#606266">{{ current?.content || '-' }}</div>
      </el-form-item>
      <el-form-item label="处理结论">
        <el-radio-group v-model="form.auditStatus" :disabled="viewOnly">
          <el-radio
            v-for="dict in handleOptions"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="处理说明">
        <el-input
          v-model="form.auditResult"
          type="textarea"
          :rows="4"
          maxlength="500"
          show-word-limit
          :disabled="viewOnly"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">关闭</el-button>
      <el-button v-if="!viewOnly" type="primary" :loading="submitting" @click="submit">确定</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/activity/report'
import * as ActivityApi from '@/api/activity/activity'
import { DICT_TYPE, getDictLabel, getIntDictOptions } from '@/utils/dict'

defineOptions({ name: 'ActivityReport' })

const route = useRoute()
const message = useMessage()
const loading = ref(false)
const submitting = ref(false)
const visible = ref(false)
const viewOnly = ref(false)
const list = ref<Api.ActivityReportVO[]>([])
const total = ref(0)
const current = ref<Api.ActivityReportVO | null>(null)
const activityList = ref<any[]>([])
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  activityId: undefined as number | undefined,
  activityTitle: '',
  reporterUserId: undefined as number | undefined,
  auditStatus: undefined as number | undefined
})
const form = reactive({ id: 0, auditStatus: 1, auditResult: '' })

// 活动列表「举报」按钮带 activityId 进来
if (route.query.activityId) {
  const activityId = Number(route.query.activityId)
  if (!Number.isNaN(activityId)) {
    query.activityId = activityId
  }
}

const handleOptions = computed(() =>
  getIntDictOptions(DICT_TYPE.ACTIVITY_REPORT_AUDIT_STATUS).filter((item) => item.value !== 0)
)

const statusTagType = (status: number) => {
  if (Number(status) === 1) return 'success'
  if (Number(status) === 2) return 'info'
  return 'warning'
}

const load = async () => {
  loading.value = true
  try {
    const params = {
      ...query,
      reporterUserId: query.reporterUserId ? Number(query.reporterUserId) : undefined
    }
    const data = await Api.getActivityReportPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  Object.assign(query, {
    pageNo: 1,
    pageSize: 10,
    activityId: undefined,
    activityTitle: '',
    reporterUserId: undefined,
    auditStatus: undefined
  })
  load()
}

const open = (row: Api.ActivityReportVO) => {
  current.value = row
  viewOnly.value = false
  form.id = row.id!
  form.auditStatus = 1
  form.auditResult = ''
  visible.value = true
}

const openView = (row: Api.ActivityReportVO) => {
  current.value = row
  viewOnly.value = true
  form.id = row.id!
  form.auditStatus = Number(row.auditStatus) || 1
  form.auditResult = row.auditResult || ''
  visible.value = true
}

const submit = async () => {
  submitting.value = true
  try {
    await Api.handleActivityReport(form)
    message.success('处理成功')
    visible.value = false
    load()
  } finally {
    submitting.value = false
  }
}

const loadActivityList = async () => {
  try {
    activityList.value = (await ActivityApi.getActivityListAll()) || []
  } catch {
    activityList.value = []
  }
}

onMounted(() => {
  loadActivityList()
  load()
})
</script>
